import type { IObservableArray } from 'mobx';
import { toJS } from 'mobx';

import type { Campaign, DisplayCreative, IDimensionDefinition } from '@feathr/blackbox';
import {
  AdTagCreative,
  CreativeClass,
  EIntegrationTypes,
  getAspectRatioNotation,
  metaAspectRatiosMatch,
  MOBILE_GEOFENCING_DIMENSION_KEYS,
  ValidDimensions,
  ValidFacebookDimensions,
  ValidFacebookVideoDimensions,
} from '@feathr/blackbox';

import { DEFAULT_AD_TAG_HTML } from './display.utils';

// =============================================================================
// Meta Creative Dimension Validation
// =============================================================================

/**
 * Result of validating a creative's dimensions against Meta's requirements.
 */
export interface IMetaValidationResult {
  /** Whether the creative passes all validation checks */
  isValid: boolean;
  /** Whether the aspect ratio matches a valid Meta spec */
  isAspectRatioValid: boolean;
  /** Whether the dimensions meet the minimum size requirements */
  meetsMinDimensions: boolean;
  /** Minimum width required for this aspect ratio */
  minWidth: number;
  /** Minimum height required for this aspect ratio */
  minHeight: number;
  /** The spec key that best matches these dimensions */
  matchingSpec: string;
}

/**
 * Finds the best matching spec for the given dimensions and aspect ratio.
 * Prefers exact match, then falls back to smallest valid spec.
 *
 * @param matchingDimensions - Array of dimensions that match the aspect ratio
 * @param width - The creative's width
 * @param height - The creative's height
 * @returns The spec key of the best matching dimension
 */
export function findMatchingSpec(
  matchingDimensions: IDimensionDefinition[],
  width: number,
  height: number,
): string {
  const exactMatch = matchingDimensions.find((dim) => dim.width === width && dim.height === height);

  if (exactMatch) {
    return exactMatch.spec;
  }

  const sortedBySize = [...matchingDimensions].sort(
    (a, b) => a.width * a.height - b.width * b.height,
  );

  const bestMatch = sortedBySize.find((dim) => dim.width >= width && dim.height >= height);
  return (bestMatch || sortedBySize[0]).spec;
}

/**
 * Validates creative dimensions against Meta's requirements.
 * Checks aspect ratio (with tolerance) and minimum dimensions.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param validDimensions - Record of valid dimension definitions for this creative type
 * @returns Validation result with details about what passed/failed
 */
export function validateMetaCreativeDimensions(
  width: number,
  height: number,
  validDimensions: Record<string, IDimensionDefinition>,
): IMetaValidationResult {
  const creativeAspectRatio = width / height;
  const validAspectRatioValues = Object.values(validDimensions).map(
    (dim) => dim.width / dim.height,
  );

  const isAspectRatioValid = validAspectRatioValues.some((validRatio) =>
    metaAspectRatiosMatch(creativeAspectRatio, validRatio),
  );

  if (!isAspectRatioValid) {
    return {
      isValid: false,
      isAspectRatioValid: false,
      meetsMinDimensions: false,
      minWidth: 0,
      minHeight: 0,
      matchingSpec: '',
    };
  }

  const matchingDimensions = Object.values(validDimensions).filter((dim) =>
    metaAspectRatiosMatch(dim.width / dim.height, creativeAspectRatio),
  );

  const minWidth = Math.min(...matchingDimensions.map((dim) => dim.width));
  const minHeight = Math.min(...matchingDimensions.map((dim) => dim.height));
  const meetsMinDimensions = width >= minWidth && height >= minHeight;
  const matchingSpec = findMatchingSpec(matchingDimensions, width, height);

  return {
    isValid: meetsMinDimensions,
    isAspectRatioValid: true,
    meetsMinDimensions,
    minWidth,
    minHeight,
    matchingSpec,
  };
}

/**
 * Finds the closest valid aspect ratio for a creative that failed validation.
 * Used to provide helpful error messages to users.
 *
 * @param width - The creative's width
 * @param height - The creative's height
 * @param validDimensions - Record of valid dimension definitions
 * @returns A string describing the closest valid aspect ratio (e.g., "1:1")
 */
export function findClosestValidAspectRatio(
  width: number,
  height: number,
  validDimensions: Record<string, IDimensionDefinition>,
): string {
  const creativeRatio = width / height;
  let closestDiff = Infinity;
  let closestDimension: IDimensionDefinition | undefined;

  for (const dimension of Object.values(validDimensions)) {
    const dimRatio = dimension.width / dimension.height;
    const diff = Math.abs(creativeRatio - dimRatio);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestDimension = dimension;
    }
  }

  if (!closestDimension) {
    return '1:1';
  }

  return getAspectRatioNotation(closestDimension.width / closestDimension.height);
}

// =============================================================================
// Display Creative Dimension Validation
// =============================================================================

/**
 * Returns the ValidDimensions subset allowed for the given campaign. Only
 * MobileGeoFencing narrows the set today; every other campaign class (and
 * calls with no campaign) see the full ValidDimensions record.
 */
function getCampaignAllowedDimensions(campaign?: Campaign): Record<string, IDimensionDefinition> {
  if (campaign?.isMobileGeoFencingCampaign) {
    return Object.fromEntries(
      Object.entries(ValidDimensions).filter(([key]) => MOBILE_GEOFENCING_DIMENSION_KEYS.has(key)),
    );
  }
  return ValidDimensions;
}

/**
 * Result of validating a creative's dimensions against Display's requirements.
 */
export interface IDisplayValidationResult {
  /** Whether the creative passes validation */
  isValid: boolean;
  /** The spec key that matches these dimensions (empty if invalid) */
  matchingSpec: string;
  /** Target dimension to resize to (when aspect ratio matches but dimensions differ) */
  targetDimension?: IDimensionDefinition;
  /** The closest valid dimension definition for helpful error messages */
  closestDimension?: IDimensionDefinition;
}

/**
 * Finds the closest valid dimension by comparing aspect ratio difference.
 * Used to provide helpful error messages when aspect ratio doesn't match.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param isVideo - Whether this is a video creative
 * @param campaign - Optional campaign; when provided, narrows the search to the
 *   dimensions valid for the campaign class (e.g. MobileGeoFencing → the
 *   mobile-geo allowlist). Omit to search the full ValidDimensions record.
 * @returns The closest matching dimension definition
 */
export function findClosestDisplayDimension(
  width: number,
  height: number,
  isVideo: boolean,
  campaign?: Campaign,
): IDimensionDefinition | undefined {
  const aspectRatio = width / height;
  let closestDiff = Infinity;
  let closestDimension: IDimensionDefinition | undefined;

  for (const dimension of Object.values(getCampaignAllowedDimensions(campaign))) {
    // Skip video-only specs for images and vice versa
    const isVideoDimension = dimension.spec.includes('video') || dimension.spec === 'video';
    if (isVideo !== isVideoDimension) {
      continue;
    }

    // Calculate aspect ratio difference
    const dimensionRatio = dimension.width / dimension.height;
    const diff = Math.abs(aspectRatio - dimensionRatio);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestDimension = dimension;
    }
  }

  return closestDimension;
}

/**
 * Finds all valid dimensions that match the given aspect ratio and are smaller
 * than or equal to the original dimensions.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param isVideo - Whether this is a video creative
 * @param campaign - Optional campaign; when provided, narrows the search to the
 *   dimensions valid for the campaign class. Omit to search full ValidDimensions.
 * @returns Array of matching dimension definitions
 */
export function findMatchingDisplayDimensions(
  width: number,
  height: number,
  isVideo: boolean,
  campaign?: Campaign,
): IDimensionDefinition[] {
  const aspectRatio = width / height;
  const results: IDimensionDefinition[] = [];

  for (const dimension of Object.values(getCampaignAllowedDimensions(campaign))) {
    // Skip video-only specs for images and vice versa
    const isVideoDimension = dimension.spec.includes('video') || dimension.spec === 'video';
    if (isVideo !== isVideoDimension) {
      continue;
    }

    const dimensionRatio = dimension.width / dimension.height;

    // Check if aspect ratio matches exactly (no tolerance for display ads)
    const ratioMatches = aspectRatio === dimensionRatio;

    // Include dimensions that are smaller or equal to the original
    const isSmallerOrEqual = dimension.width <= width && dimension.height <= height;

    if (ratioMatches && isSmallerOrEqual) {
      results.push(dimension);
    }
  }

  return results;
}

/**
 * Finds the largest valid dimension that matches the given aspect ratio.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param isVideo - Whether this is a video creative
 * @param campaign - Optional campaign; when provided, narrows the search to the
 *   dimensions valid for the campaign class. Omit to search full ValidDimensions.
 * @returns The largest matching dimension, or undefined if none match
 */
export function findLargestMatchingDisplayDimension(
  width: number,
  height: number,
  isVideo: boolean,
  campaign?: Campaign,
): IDimensionDefinition | undefined {
  const matchingDimensions = findMatchingDisplayDimensions(width, height, isVideo, campaign);

  if (matchingDimensions.length === 0) {
    return undefined;
  }

  // Sort by total pixels (area) descending and return the largest
  return [...matchingDimensions].sort((a, b) => b.width * b.height - a.width * a.height)[0];
}

/**
 * Validates creative dimensions against Display advertising requirements.
 * Uses aspect ratio matching with tolerance, finding the largest valid dimension
 * that the uploaded image can be resized to.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param isVideo - Whether this is a video creative
 * @param campaign - Optional campaign; when provided, exact-match, aspect-ratio,
 *   and closest-size lookups are all scoped to the dimensions valid for the
 *   campaign class (e.g. MobileGeoFencing → the mobile-geo allowlist). Omit for
 *   full ValidDimensions behavior.
 * @returns Validation result with target dimension for resizing
 *
 * @example
 * ```typescript
 * // Exact match
 * const result = validateDisplayCreativeDimensions(300, 250, false);
 * // result.isValid === true, result.matchingSpec === 'medium_rectangle'
 *
 * // 2x size that matches aspect ratio - will be resized
 * const result2x = validateDisplayCreativeDimensions(600, 500, false);
 * // result2x.isValid === true, result2x.targetDimension.width === 300
 *
 * // Invalid aspect ratio
 * const invalidResult = validateDisplayCreativeDimensions(500, 400, false);
 * // invalidResult.isValid === false, invalidResult.closestDimension provided
 * ```
 */
export function validateDisplayCreativeDimensions(
  width: number,
  height: number,
  isVideo: boolean,
  campaign?: Campaign,
): IDisplayValidationResult {
  // Exact-match lookup is scoped to the campaign's allowed dimensions so that a size
  // valid globally but not for this campaign (e.g. 728x90 on MobileGeoFencing) fails
  // here instead of passing upload only to be rejected by the wizard downstream.
  const allowedDimensions = getCampaignAllowedDimensions(campaign);
  const key = `${width}x${height}`;
  const exactMatch = allowedDimensions[key];

  if (exactMatch) {
    // For videos, ensure the matched dimension is a video spec
    const isVideoDimension = exactMatch.spec.includes('video') || exactMatch.spec === 'video';
    if (isVideo && !isVideoDimension) {
      // Video uploaded with image dimensions - find largest matching video dimension
      const largestMatch = findLargestMatchingDisplayDimension(width, height, true, campaign);
      if (largestMatch) {
        return {
          isValid: true,
          matchingSpec: largestMatch.spec,
          targetDimension: largestMatch,
        };
      }
      return {
        isValid: false,
        matchingSpec: '',
        closestDimension: findClosestDisplayDimension(width, height, true, campaign),
      };
    }
    if (!isVideo && isVideoDimension) {
      /*
       * Image uploaded at dimensions that only exist as a video spec (e.g.
       * 640x480 = 480p_video). Don't hand back the video spec — the creatives
       * step filters to image specs and would then reject it, disabling Next
       * with "does not match any standard ad size" after a "successful" upload.
       * Match an image dimension by aspect ratio, or fail here. See #8442.
       */
      const largestMatch = findLargestMatchingDisplayDimension(width, height, false, campaign);
      if (largestMatch) {
        return {
          isValid: true,
          matchingSpec: largestMatch.spec,
          targetDimension: largestMatch,
        };
      }
      return {
        isValid: false,
        matchingSpec: '',
        closestDimension: findClosestDisplayDimension(width, height, false, campaign),
      };
    }

    return {
      isValid: true,
      matchingSpec: exactMatch.spec,
    };
  }

  // No exact match - find the largest dimension that matches the aspect ratio
  const largestMatch = findLargestMatchingDisplayDimension(width, height, isVideo, campaign);

  if (largestMatch) {
    return {
      isValid: true,
      matchingSpec: largestMatch.spec,
      targetDimension: largestMatch,
    };
  }

  // No matching aspect ratio - find closest for helpful error message
  return {
    isValid: false,
    matchingSpec: '',
    closestDimension: findClosestDisplayDimension(width, height, isVideo, campaign),
  };
}

/**
 * Checks if a creative has valid dimensions.
 * For display creatives, checks exact match against ValidDimensions.
 * For Facebook creatives, checks aspect ratio with tolerance and minimum size.
 * @param creative - The DisplayCreative model
 * @returns True if the creative has valid dimensions
 */
export function hasValidDimensions(creative: DisplayCreative): boolean {
  const width = creative.get('width');
  const height = creative.get('height');
  if (!width || !height) {
    return false;
  }

  const cls = creative.get('_cls');

  if (cls === CreativeClass.FacebookImage) {
    return validateMetaCreativeDimensions(width, height, ValidFacebookDimensions).isValid;
  }

  if (cls === CreativeClass.FacebookVideo) {
    return validateMetaCreativeDimensions(width, height, ValidFacebookVideoDimensions).isValid;
  }

  const key = `${width}x${height}`;
  return key in ValidDimensions;
}

// =============================================================================
// Creative Step Validation
// =============================================================================

/**
 * Checks whether a creative's dimensions match any of the campaign's valid
 * dimensions by exact aspect ratio with minimum-dimension thresholds.
 *
 * @param creative - The creative to check
 * @param validDimensions - The campaign's valid dimensions keyed by spec
 * @returns True if the creative matches any valid aspect ratio
 */
export function hasValidAspectRatio(
  creative: DisplayCreative,
  validDimensions: Record<string, IDimensionDefinition>,
): boolean {
  const width = creative.get('width');
  const height = creative.get('height');
  if (!width || !height) {
    return false;
  }
  const aspectRatio = width / height;
  return Object.values(validDimensions).some((dimension) => {
    const targetAspectRatio = dimension.width / dimension.height;
    return (
      aspectRatio === targetAspectRatio && width >= dimension.width && height >= dimension.height
    );
  });
}

/**
 * Validates the Creatives step of the ad wizard for a TTD-flavored campaign.
 *
 * Returns the first error encountered for each creative plus any step-level
 * errors (missing destination URL, no enabled creatives, Omni ad-group
 * coverage, Meta square-creative requirement). Error strings are intentionally
 * hardcoded English to preserve existing behavior; i18n consolidation is a
 * separate follow-up.
 *
 * Not to be confused with the same-named function in
 * `FacebookCreativesStep.utils.ts`, which validates `FacebookCampaign` /
 * `FacebookDisplayCreative` instances and lives in `FacebookWizard/`.
 *
 * @param campaign - The TTD campaign to validate
 * @param creatives - The campaign's creatives
 * @returns An array of error strings (empty when valid)
 */
export function validateStepCreatives(
  campaign: Campaign,
  creatives: IObservableArray<DisplayCreative>,
): string[] {
  // Use a Set so the same error reported by multiple creatives (e.g. missing
  // destination URL) only shows once in the wizard's error list.
  const errors = new Set<string>();

  // Use validate directly instead of isValid to avoid computedFn warning when called outside reactive context
  const destinationUrlValidation = campaign.validate(['destination_url'], false);
  destinationUrlValidation.errors.forEach((error) => errors.add(error));

  const nonArchivedCreatives = creatives.filter((c) => !c.get('is_archived'));
  const enabledCreatives = creatives.filter((c) => c.get('is_enabled'));

  // Pick one or the other so the user doesn't see both "no creative" and
  // "no enabled creative" when there are simply no creatives at all.
  if (nonArchivedCreatives.length < 1) {
    errors.add('You need at least one creative.');
  } else if (enabledCreatives.length < 1) {
    errors.add('You need at least one enabled creative.');
  }

  if (campaign.isOmniCampaign) {
    const creativesWithoutAudiences = nonArchivedCreatives.filter(
      (c) => c.get('ad_groups') === null || c.get('ad_groups') === undefined,
    );
    if (creativesWithoutAudiences.length > 0) {
      errors.add('You need to select at least one audience for each creative.');
    }

    const hasMainGroup = nonArchivedCreatives.some((c) => {
      const adGroups = c.get('ad_groups');
      // Empty array means targeting both groups.
      if (Array.isArray(adGroups) && adGroups.length === 0) {
        return true;
      }
      return adGroups?.includes('main') ?? false;
    });
    const hasLookalikeGroup = nonArchivedCreatives.some((c) => {
      const adGroups = c.get('ad_groups');
      if (Array.isArray(adGroups) && adGroups.length === 0) {
        return true;
      }
      return adGroups?.includes('lookalike') ?? false;
    });

    const missingGroups: string[] = [];
    if (!hasMainGroup) {
      missingGroups.push('Targeted groups');
    }
    if (!hasLookalikeGroup) {
      missingGroups.push('Lookalike audiences');
    }
    if (missingGroups.length > 0) {
      errors.add(`You need at least one creative assigned to ${missingGroups.join(' and ')}`);
    }
  }

  const integrations = campaign.get('integrations');
  if (integrations?.includes(EIntegrationTypes.Meta)) {
    const hasSquareCreative = creatives.some((c) => {
      const w = c.get('width');
      const h = c.get('height');
      return !c.get('is_archived') && c.get('is_enabled') && w === h && w > 0;
    });
    if (!hasSquareCreative) {
      errors.add('Meta campaigns require at least one square (1:1) creative.');
    }
  }

  creatives.forEach((crv) => {
    const fieldErrors = toJS(
      crv.validate(['width', 'height', 'spec', 'destination_url', 'name'], false).errors,
    );
    fieldErrors.forEach((error) => errors.add(error));

    const validDimensions = crv.getValidDimensions(campaign);
    const specs = Object.values(validDimensions).map((v) => v.spec);
    const creativeIsValidDimensions = specs.includes(crv.get('spec'));
    const creativeHasValidAspectRatio = hasValidAspectRatio(crv, validDimensions);

    if (!creativeIsValidDimensions && !creativeHasValidAspectRatio) {
      errors.add(
        `${crv.get('width')}x${crv.get('height')} does not match any standard ad size. Images must have exact aspect ratios and meet minimum dimensions.`,
      );
    }
    if (crv instanceof AdTagCreative && crv.get('_cls') === CreativeClass.DisplayAdTag) {
      validateAdTagForStep(crv).forEach((error) => errors.add(error));
    }
  });

  return Array.from(errors);
}

/**
 * Step-level ad-tag validation with hardcoded English messages, used only by
 * `validateStepCreatives` to preserve the legacy step-error wording.
 *
 * The i18n-aware version (`validateAdTagCreative` in
 * `CreativeModal/CreativeModal.utils.ts`) is the right choice for any new
 * code; this inlined copy exists to avoid threading `t` through the entire
 * wizard validation chain in this PR.
 */
function validateAdTagForStep(creative: AdTagCreative): string[] {
  const errors: string[] = [];
  const adtagHTML = creative.get('adtag');
  if (!adtagHTML) {
    errors.push('Ad tag HTML cannot be empty.');
    return errors;
  }
  if (!adtagHTML.includes('@FEATHR_TRACK@')) {
    errors.push('Ad tag HTML must include the @FEATHR_TRACK@ impression tracking macro.');
  }
  if (!(adtagHTML.includes('@FEATHR_CLICK@') || adtagHTML.includes('@FEATHR_CLICK_ESC@'))) {
    errors.push(
      'Ad tag HTML must include a click-through tracking macro: @FEATHR_CLICK@ or @FEATHR_CLICK_ESC@.',
    );
  }
  if (adtagHTML === DEFAULT_AD_TAG_HTML) {
    errors.push('Ad tag HTML must be different than the default HTML');
  }
  return errors;
}
